import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  Card,
  Button,
  Space,
  Typography,
  Grid,
  Select,
  Row,
  Col,
  Empty,
  Tag,
  Input,
  Divider,
  Tooltip,
  Modal,
  Form,
  Radio,
  InputNumber,
  message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FileTextOutlined } from '@ant-design/icons';
import { Translate } from 'react-jhipster';
import axios from 'axios';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { createEvaluation, getAllEvaluations, updateEvaluation } from 'app/entities/evaluation/evaluation.reducer';
import { getClasse } from 'app/entities/classe/classe.reducer';
import { getMatieresByClasseId } from 'app/entities/matiere-ecole/matiere-ecole.reducer';
import { getPeriodes } from 'app/entities/periode-evaluation/periode-evaluation.reducer';
import { getSousMatieres, reset as resetSousMatieres } from 'app/entities/sous-matiere/sous-matiere.reducer';
import { EvaluationType, IEvaluation } from 'app/shared/model/evaluation.model';
import { StatutPeriodeEvaluation } from 'app/shared/model/periode-evaluation.model';

const { Title } = Typography;
const { useBreakpoint } = Grid;

const getEvaluationDateSortValue = (date?: string | null) => {
  if (!date) return Number.NEGATIVE_INFINITY;
  const timestamp = Date.parse(`${date}T00:00:00`);
  return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
};

const getPeriodeStatusTag = (statut?: StatutPeriodeEvaluation) => {
  if (statut === StatutPeriodeEvaluation.OUVERTE) return <Tag color="green">Ouverte</Tag>;
  if (statut === StatutPeriodeEvaluation.FERMEE) return <Tag color="red">Fermée</Tag>;
  return <Tag color="blue">Planifiée</Tag>;
};

const formatDateLabel = (value?: string | null) => {
  if (!value) return 'Date non définie';
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }).format(date);
};

interface ClasseEvaluationsProps {
  ecoleid: string;
  classeid: string;
  anneeScolaireId: string;
  active: boolean;
}

const ClasseEvaluations = ({ ecoleid, classeid, anneeScolaireId, active }: ClasseEvaluationsProps) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const evaluations = useAppSelector(state => state.evaluation.entities);
  const loading = useAppSelector(state => state.evaluation.loading);
  const updating = useAppSelector(state => state.evaluation.updating);
  const classeEntity = useAppSelector(state => state.classe.entity);
  const matieres = useAppSelector(state => state.matiereEcole.entities);
  const sousMatieres = useAppSelector(state => state.sousMatiere.entities);
  const periodes = useAppSelector(state => state.periodeEvaluation.entities);
  const screens = useBreakpoint();
  const defaultPeriodeAppliedRef = useRef(false);
  const [createEvaluationForm] = Form.useForm();

  const [periodeId, setPeriodeId] = useState<string | null>(null);
  const [matiereId, setMatiereId] = useState<string | null>(null);
  const [type, setType] = useState<EvaluationType | null>(null);
  const [search, setSearch] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedEvaluation, setSelectedEvaluation] = useState<IEvaluation | null>(null);
  const [selectedFormPeriodeId, setSelectedFormPeriodeId] = useState<string | null>(null);
  const [selectedCreateMatiereId, setSelectedCreateMatiereId] = useState<string | null>(null);
  const [matiereNameMap, setMatiereNameMap] = useState<Record<string, string>>({});
  const [noteStatusMap, setNoteStatusMap] = useState<Record<string, 'Non saisie' | 'En cours' | 'Terminée'>>({});
  const currentPeriode = useMemo(() => periodes.find(periode => periode.statut === StatutPeriodeEvaluation.OUVERTE) || null, [periodes]);
  const formPeriode = useMemo(
    () => (selectedEvaluation ? periodes.find(periode => periode.id === selectedFormPeriodeId) || null : currentPeriode),
    [currentPeriode, periodes, selectedEvaluation, selectedFormPeriodeId]
  );
  const selectedCreateMatiere = useMemo(
    () => matieres.find(matiere => matiere.id === selectedCreateMatiereId),
    [matieres, selectedCreateMatiereId]
  );
  const selectedCreateMatiereAvecSousMatieres = Boolean(selectedCreateMatiere?.avecSousMatieres);

  useEffect(() => {
    if (!active) return;
    dispatch(getClasse(classeid));
    dispatch(getMatieresByClasseId(classeid));
  }, [active, classeid]);

  useEffect(() => {
    const map: Record<string, string> = {};
    (matieres || []).forEach(m => {
      if (m.id) map[m.id] = m.nom || '';
    });
    setMatiereNameMap(map);
  }, [matieres]);

  useEffect(() => {
    if (!active) return;
    if (classeEntity?.id === classeid && classeEntity?.cycleId && anneeScolaireId) {
      dispatch(getPeriodes({ ecoleId: ecoleid, anneeScolaireId, cycleId: classeEntity.cycleId }));
    }
  }, [active, classeEntity?.id, classeEntity?.cycleId, classeid, anneeScolaireId, ecoleid]);

  useEffect(() => {
    defaultPeriodeAppliedRef.current = false;
    setPeriodeId(null);
  }, [classeid, anneeScolaireId]);

  useEffect(() => {
    if (!active || defaultPeriodeAppliedRef.current || !periodes.length) return;

    if (currentPeriode?.id) {
      setPeriodeId(currentPeriode.id);
    }
    defaultPeriodeAppliedRef.current = true;
  }, [active, currentPeriode?.id, periodes.length]);

  useEffect(() => {
    if (!active) return;
    if (anneeScolaireId) {
      dispatch(getAllEvaluations({ ecoleId: ecoleid, anneeScolaireId }));
    }
  }, [active, anneeScolaireId, ecoleid]);

  const isDateWithinFormPeriode = (date?: string | null) => {
    if (!date || !formPeriode?.dateDebut || !formPeriode?.dateFin) {
      return true;
    }
    return date >= formPeriode.dateDebut && date <= formPeriode.dateFin;
  };

  const openCreateModal = () => {
    createEvaluationForm.resetFields();
    createEvaluationForm.setFieldsValue({ type: EvaluationType.DEVOIR });
    setSelectedEvaluation(null);
    setSelectedFormPeriodeId(currentPeriode?.id || null);
    setSelectedCreateMatiereId(null);
    dispatch(resetSousMatieres());
    setCreateModalOpen(true);
  };

  const openEditModal = (evaluation: IEvaluation) => {
    createEvaluationForm.resetFields();
    createEvaluationForm.setFieldsValue({
      type: evaluation.type,
      matiereId: evaluation.matiereId,
      sousMatiereId: evaluation.sousMatiereId || undefined,
      bareme: evaluation.bareme,
      titre: evaluation.titre,
      date: evaluation.date,
      periodeId: evaluation.periodeId,
    });
    setSelectedEvaluation(evaluation);
    setSelectedFormPeriodeId(evaluation.periodeId || null);
    setSelectedCreateMatiereId(evaluation.matiereId || null);
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setCreateModalOpen(false);
    createEvaluationForm.resetFields();
    setSelectedEvaluation(null);
    setSelectedFormPeriodeId(null);
    setSelectedCreateMatiereId(null);
    dispatch(resetSousMatieres());
  };

  useEffect(() => {
    if (createModalOpen && classeEntity?.cycleId && selectedCreateMatiereId && selectedCreateMatiereAvecSousMatieres) {
      dispatch(
        getSousMatieres({
          cycleMatiereId: selectedCreateMatiere?.cycleMatiereId,
          cycleId: classeEntity.cycleId,
          matiereEcoleId: selectedCreateMatiereId,
        })
      );
    } else {
      dispatch(resetSousMatieres());
    }
  }, [
    createModalOpen,
    classeEntity?.cycleId,
    selectedCreateMatiereId,
    selectedCreateMatiere?.cycleMatiereId,
    selectedCreateMatiereAvecSousMatieres,
  ]);

  const handleSaveEvaluation = async values => {
    if (!selectedEvaluation && !currentPeriode?.id) {
      message.error("Vous devez ouvrir une période d'évaluation avant de créer une évaluation.");
      return;
    }
    if (!isDateWithinFormPeriode(values.date)) {
      message.error(
        `La date doit être comprise entre le ${formatDateLabel(formPeriode?.dateDebut)} et le ${formatDateLabel(formPeriode?.dateFin)}`
      );
      return;
    }
    if (selectedCreateMatiereAvecSousMatieres && sousMatieres.length && !values.sousMatiereId) {
      message.error('La sous-matière est requise pour cette matière.');
      return;
    }

    try {
      const action = selectedEvaluation?.id ? updateEvaluation : createEvaluation;
      await dispatch(
        action({
          ...selectedEvaluation,
          type: values.type,
          matiereId: values.matiereId,
          bareme: values.bareme,
          titre: values.titre,
          date: values.date,
          sousMatiereId: selectedCreateMatiereAvecSousMatieres ? values.sousMatiereId : undefined,
          classeId: classeid,
          periodeId: selectedEvaluation ? values.periodeId : currentPeriode?.id,
          ecoleId: ecoleid,
          anneeScolaireId,
        })
      ).unwrap();
      message.success(selectedEvaluation ? 'Évaluation modifiée' : 'Évaluation créée');
      closeCreateModal();
    } catch (error) {
      message.error(selectedEvaluation ? "Impossible de modifier l'évaluation." : "Impossible de créer l'évaluation.");
    }
  };

  const filteredEvaluations = useMemo(() => {
    return evaluations
      .filter(e => {
        if (e.classeId !== classeid) return false;
        if (periodeId && e.periodeId !== periodeId) return false;
        if (matiereId && e.matiereId !== matiereId) return false;
        if (type && e.type !== type) return false;
        if (search && !`${e.titre || ''}`.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
      })
      .sort((a, b) => getEvaluationDateSortValue(b.date) - getEvaluationDateSortValue(a.date));
  }, [evaluations, classeid, periodeId, matiereId, type, search]);

  useEffect(() => {
    if (!active) return;
    const loadStatuses = async () => {
      if (!filteredEvaluations.length) {
        setNoteStatusMap({});
        return;
      }
      const missing = filteredEvaluations.filter(e => e.id && !noteStatusMap[e.id]);
      if (missing.length === 0) return;

      const ids = missing.map(e => e.id).filter(Boolean);
      if (!ids.length) return;
      const response = await axios.post('/api/notes/evaluations/status', ids);
      const statusEntries = (response.data || []).map(item => [item.evaluationId, item.statut || 'Non saisie'] as const);

      const next = { ...noteStatusMap };
      statusEntries.forEach(entry => {
        if (!entry) return;
        next[entry[0]] = entry[1];
      });
      setNoteStatusMap(next);
    };

    loadStatuses();
  }, [active, filteredEvaluations]);

  const columns = [
    { title: 'Titre', dataIndex: 'titre', key: 'titre' },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render(t) {
        return t ? (
          <Tag color={t === EvaluationType.COMPOSITION ? 'blue' : t === EvaluationType.CONTROLE ? 'green' : 'gold'}>{t}</Tag>
        ) : (
          '-'
        );
      },
    },
    {
      title: 'Matière',
      dataIndex: 'matiereId',
      key: 'matiereId',
      render(id, record) {
        const matiereNom = matiereNameMap[id] || record.matiereNom || '-';
        return record.sousMatiereNom ? `${matiereNom} / ${record.sousMatiereNom}` : matiereNom;
      },
    },
    {
      title: 'Statut notes',
      dataIndex: 'id',
      key: 'notesStatus',
      render(id) {
        const statut = noteStatusMap[id] || 'Non saisie';
        const color = statut === 'Terminée' ? 'green' : statut === 'En cours' ? 'gold' : 'red';
        return <Tag color={color}>{statut}</Tag>;
      },
    },
    { title: 'Date', dataIndex: 'date', key: 'date' },
    {
      title: 'Actions',
      key: 'actions',
      width: screens.xs ? 100 : 200,
      render(_, record) {
        const statut = record.id ? noteStatusMap[record.id] : undefined;
        const isComplete = statut === 'Terminée';
        return (
          <Space size="middle">
            <Tooltip title="Editer l'évaluation">
              <Button type="text" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
            </Tooltip>
            <Tooltip title="Supprimer l'évaluation">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={isComplete}
                onClick={() => history.push(`/evaluation/${ecoleid}/${anneeScolaireId}/${record.id}/delete?classeId=${classeid}`)}
              />
            </Tooltip>
            <Tooltip title="Saisir les notes">
              <Button
                type="text"
                icon={<FileTextOutlined />}
                onClick={() =>
                  history.push(`/notes/${ecoleid}/${anneeScolaireId}?evaluationId=${record.id}&classeId=${classeid}&returnTo=classe`)
                }
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <Card style={{ margin: screens.xs ? '16px 8px' : '24px 24px' }} bodyStyle={{ padding: screens.xs ? '0 8px 16px' : '0 24px 24px' }}>
      <div
        style={{
          padding: screens.xs ? '16px 0' : '24px 0',
          display: 'flex',
          flexDirection: screens.xs ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: screens.xs ? 'flex-start' : 'center',
          gap: screens.xs ? '16px' : '0',
        }}
      >
        <Title level={2} style={{ margin: 0, fontSize: screens.xs ? '1.5rem' : '2rem' }}>
          <Translate contentKey="korischoolApp.evaluation.home.title">Évaluations</Translate>
        </Title>
        <Space>
          <Button
            className="d-flex align-items-center justify-content-between gap-3 p-1 rounded"
            onClick={openCreateModal}
            style={{
              backgroundColor: '#F28E19',
              color: '#fff',
              fontWeight: 'bold',
              padding: screens.xs ? '6px 8px' : '8px 16px',
              fontSize: screens.xs ? '12px' : '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            <PlusOutlined style={{ fontSize: screens.xs ? '12px' : '14px', marginRight: screens.xs ? '4px' : '8px' }} />
            <Translate contentKey="korischoolApp.evaluation.home.createLabel">Create new Evaluation</Translate>
          </Button>
        </Space>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Select
              placeholder="Période"
              style={{ width: '100%' }}
              value={periodeId || undefined}
              onChange={v => setPeriodeId(v || null)}
              allowClear
            >
              {periodes.map(periode => (
                <Select.Option key={periode.id} value={periode.id}>
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span>{periode.nom}</span>
                    {getPeriodeStatusTag(periode.statut)}
                  </span>
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={8}>
            <Select
              placeholder="Matière"
              style={{ width: '100%' }}
              value={matiereId || undefined}
              onChange={v => setMatiereId(v || null)}
              allowClear
            >
              {matieres.map(matiere => (
                <Select.Option key={matiere.id} value={matiere.id}>
                  {matiere.nom}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={8}>
            <Select placeholder="Type" style={{ width: '100%' }} value={type || undefined} onChange={v => setType(v || null)} allowClear>
              <Select.Option value={EvaluationType.DEVOIR}>Devoir</Select.Option>
              <Select.Option value={EvaluationType.COMPOSITION}>Composition</Select.Option>
              <Select.Option value={EvaluationType.CONTROLE}>Contrôle</Select.Option>
            </Select>
          </Col>
        </Row>
      </Card>

      <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
        <Col>
          <Typography.Text type="secondary">{filteredEvaluations.length} évaluation(s)</Typography.Text>
        </Col>
      </Row>

      <Divider style={{ margin: '8px 0' }} />

      {filteredEvaluations.length === 0 && !loading ? (
        <Empty description="Aucune évaluation trouvée" />
      ) : (
        <Table
          dataSource={filteredEvaluations}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 20 }}
          loading={loading}
          locale={{
            emptyText: <Translate contentKey="korischoolApp.evaluation.home.notFound">No evaluations found</Translate>,
          }}
          bordered
          size="middle"
          style={{ backgroundColor: 'white' }}
          scroll={{ x: screens.xs ? 'true' : 'false' }}
        />
      )}

      <Modal
        title={selectedEvaluation ? "Modifier l'évaluation" : 'Créer une évaluation'}
        visible={createModalOpen}
        onCancel={closeCreateModal}
        footer={null}
        destroyOnClose
        width={600}
      >
        <div
          style={{
            marginBottom: 20,
            borderRadius: 8,
            overflow: 'hidden',
            border: formPeriode ? '1px solid rgba(15, 118, 110, 0.18)' : '1px solid rgba(180, 83, 9, 0.18)',
            background: formPeriode ? '#f0fdf4' : '#fff7ed',
          }}
        >
          <div
            style={{
              padding: '16px 18px',
              background: formPeriode ? 'rgba(15,118,110,0.08)' : 'rgba(234,88,12,0.08)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 12, color: formPeriode ? '#0f766e' : '#b45309', fontWeight: 700, marginBottom: 4 }}>
                  Période d&apos;évaluation
                </div>
                <Typography.Title level={4} style={{ margin: 0 }}>
                  {formPeriode?.nom || 'Aucune période ouverte'}
                </Typography.Title>
              </div>
              {formPeriode ? getPeriodeStatusTag(formPeriode.statut) : <Tag color="orange">Indisponible</Tag>}
            </div>
          </div>
          <div style={{ padding: '14px 18px' }}>
            {formPeriode ? (
              <>
                <Row gutter={[12, 12]}>
                  <Col xs={24} sm={8}>
                    <Typography.Text type="secondary">Début</Typography.Text>
                    <div style={{ fontWeight: 700 }}>{formatDateLabel(formPeriode.dateDebut)}</div>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Typography.Text type="secondary">Fin</Typography.Text>
                    <div style={{ fontWeight: 700 }}>{formatDateLabel(formPeriode.dateFin)}</div>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Typography.Text type="secondary">Statut</Typography.Text>
                    <div>{getPeriodeStatusTag(formPeriode.statut)}</div>
                  </Col>
                </Row>
                <div style={{ marginTop: 12, color: '#134e4a', fontWeight: 600 }}>
                  La date saisie doit être comprise entre le {formatDateLabel(formPeriode.dateDebut)} et le{' '}
                  {formatDateLabel(formPeriode.dateFin)}.
                </div>
              </>
            ) : (
              <Typography.Text style={{ color: '#7c2d12', fontWeight: 600 }}>
                Vous devez ouvrir une période d&apos;évaluation avant de pouvoir créer une nouvelle évaluation.
              </Typography.Text>
            )}
          </div>
        </div>
        <Form form={createEvaluationForm} layout="vertical" onFinish={handleSaveEvaluation}>
          {selectedEvaluation && (
            <Form.Item label="Période" name="periodeId" rules={[{ required: true, message: 'La période est requise' }]}>
              <Select
                placeholder="Sélectionnez une période"
                onChange={(value: string) => setSelectedFormPeriodeId(value || null)}
                options={periodes.map(periode => ({ value: periode.id, label: periode.nom }))}
              />
            </Form.Item>
          )}
          <Form.Item label="Type" name="type" rules={[{ required: true, message: 'Le type est requis' }]}>
            <Radio.Group buttonStyle="solid">
              <Radio.Button value={EvaluationType.DEVOIR}>Devoir</Radio.Button>
              <Radio.Button value={EvaluationType.COMPOSITION}>Composition</Radio.Button>
              <Radio.Button value={EvaluationType.CONTROLE}>Contrôle</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item label="Matière" name="matiereId" rules={[{ required: true, message: 'La matière est requise' }]}>
            <Select
              placeholder="Rechercher une matière"
              showSearch
              optionFilterProp="children"
              onChange={value => {
                setSelectedCreateMatiereId(value || null);
                createEvaluationForm.setFieldsValue({ sousMatiereId: undefined });
              }}
              filterOption={(input, option) =>
                String(option?.children || '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {matieres.map(matiere => (
                <Select.Option key={matiere.id} value={matiere.id}>
                  {matiere.nom}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {selectedCreateMatiereAvecSousMatieres && sousMatieres.length > 0 && (
            <Form.Item label="Sous-matière" name="sousMatiereId" rules={[{ required: true, message: 'La sous-matière est requise' }]}>
              <Select placeholder="Sélectionner une sous-matière" showSearch optionFilterProp="children">
                {sousMatieres.map(sousMatiere => (
                  <Select.Option key={sousMatiere.id} value={sousMatiere.id}>
                    {sousMatiere.nom}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item label="Barème" name="bareme" rules={[{ required: true, message: 'Le barème est requis' }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="Titre" name="titre" rules={[{ required: true, message: 'Le titre est requis' }]}>
            <Input placeholder="Titre de l'évaluation" />
          </Form.Item>

          <Form.Item
            label="Date"
            name="date"
            rules={[
              { required: true, message: "La date de l'évaluation est requise" },
              {
                validator: (_, value) =>
                  isDateWithinFormPeriode(value)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          `La date doit être comprise entre le ${formatDateLabel(formPeriode?.dateDebut)} et le ${formatDateLabel(
                            formPeriode?.dateFin
                          )}`
                        )
                      ),
              },
            ]}
          >
            <Input type="date" min={formPeriode?.dateDebut || undefined} max={formPeriode?.dateFin || undefined} />
          </Form.Item>

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={updating}
              disabled={!selectedEvaluation && !currentPeriode?.id}
              style={{ backgroundColor: '#F28E19', borderColor: '#F28E19' }}
            >
              Enregistrer
            </Button>
          </Space>
        </Form>
      </Modal>
    </Card>
  );
};

export default ClasseEvaluations;
